import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SPAButton from '../shared/SPAButton';
import SPAPageHeader from '../shared/SPAPageHeader';
import SPASectionHeading from '../shared/SPASectionHeading';
import SPASelect from '../shared/SPASelect';
import compactSelectStyles from '../shared/compactSelectStyles';
import { PanelSkeleton } from '../shared/formPanel';
import DefaultsForm from './DefaultsForm';
import FeatureTotals from './FeatureTotals';
import PluginHealth from './PluginHealth';
import RawConfig from './RawConfig';
import SetUserLimitForm from './SetUserLimitForm';
import SummaryStrip from './SummaryStrip';
import ThrottledNow from './ThrottledNow';
import TopConsumers from './TopConsumers';
import UsageChart from './UsageChart';
import UserLimitsTable from './UserLimitsTable';
import {
  SetUserLimitInput, useAIPolicy, useAIPolicyMutations, useAIUsage,
} from './useAIPolicy';

const RANGES = [7, 14, 30].map(d => ({ label: `Last ${d} days`, value: d }));

const AIPolicyView = () => {
  const { data, error, isLoading } = useAIPolicy();
  const {
    clearUserLimit, errorFrom, setUserLimit, updateDefaults,
  } = useAIPolicyMutations();
  const [failure, setFailure] = useState<string | null>(null);
  const [identity, setIdentity] = useState('');
  const [days, setDays] = useState(7);
  const usage = useAIUsage(days);
  const limitFormRef = useRef<HTMLDivElement>(null);

  const startSetLimit = (id: string) => {
    setIdentity(id);
    limitFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const isSaving = setUserLimit.isLoading || clearUserLimit.isLoading || updateDefaults.isLoading;
  // The only thing worth holding locally is the last failure, in the gateway's own words.
  function run<T>(mutation: { mutate: (v: T, o: object) => void }, vars: T, onSuccess?: () => void) {
    setFailure(null);
    mutation.mutate(vars, { onError: (e: unknown) => setFailure(errorFrom(e)), onSuccess });
  }

  const failed = !isLoading && (Boolean(error) || !data);
  const userLimitCount = data ? Object.keys(data.user_limits).length : 0;

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">
        <SPAPageHeader
          actions={(
            <div className="tw-flex tw-items-center tw-gap-2">
              <SPAButton asChild variant="secondary">
                <Link to="/langfuse">Langfuse</Link>
              </SPAButton>
              {data?.gateway_url && (
                <SPAButton asChild variant="secondary">
                  <a href={data.gateway_url} rel="noreferrer" target="_blank">Bifrost</a>
                </SPAButton>
              )}
              <div className="tw-w-44">
                <SPASelect
                  aria-label="Usage window"
                  options={RANGES}
                  styles={compactSelectStyles}
                  value={RANGES.find(r => r.value === days)}
                  onChange={(option: { value: number } | null) => setDays(option?.value ?? 7)}
                />
              </div>
            </div>
          )}
          title="AI Policy"
        />
        <p className="tw-mb-5 tw-mt-0 tw-text-sm tw-text-spa-secondary">
          Per-user AI spend and rate limits, enforced by the gateway&apos;s user-spend-limiter plugin.
          Usage is token volume from Plato&apos;s own completion records, not spend.
        </p>

        {isLoading && (
          <>
            <SummaryStrip days={days} />
            <PanelSkeleton />
          </>
        )}
        {failed && <p className="tw-m-0 tw-text-sm tw-text-spa-error">{errorFrom(error)}</p>}
        {data && !failed && (
          <>
            <SummaryStrip days={days} snapshot={data} usage={usage.data} />
            <PluginHealth logs={data.logs} status={data.status} />
            {failure && <p className="tw-mb-5 tw-mt-0 tw-text-sm tw-text-spa-error" role="alert">{failure}</p>}

            <div className="tw-flex tw-flex-col tw-gap-6">
              <UsageChart daily={usage.data?.daily} />
              <TopConsumers consumers={usage.data?.top_consumers} days={days} onSelect={startSetLimit} />
              <div className="tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-2">
                <ThrottledNow users={usage.data?.throttled} />
                <FeatureTotals byFeature={usage.data?.by_feature} />
              </div>

              <DefaultsForm
                key={`${data.defaults.daily_limit_usd}/${data.defaults.requests_per_minute}`}
                defaults={data.defaults}
                isSaving={isSaving}
                onSave={(daily, rpm) => run(updateDefaults, { daily_limit_usd: daily, requests_per_minute: rpm })}
              />

              <section>
                <SPASectionHeading meta={`${userLimitCount} with their own limits`} title="Per-user limits" />
                <UserLimitsTable
                  isSaving={isSaving}
                  userLimits={data.user_limits}
                  onClear={id => run(clearUserLimit, id)}
                />
                <div ref={limitFormRef} className="tw-mt-4">
                  <SetUserLimitForm
                    identity={identity}
                    isSaving={isSaving}
                    onIdentityChange={setIdentity}
                    onSubmit={(input: SetUserLimitInput, onSaved) => run(setUserLimit, input, onSaved)}
                  />
                </div>
              </section>

              <RawConfig config={data.config} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AIPolicyView;
